// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AppEvent } from '@interfaces/interfaces';
import { JobTitle, User, UserTypes } from '@interfaces/user.interfaces';
import { EnrollmentService } from '@services/enrollment.service';
import { ErrorService } from '@services/error.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { UserService } from '@services/user.service';
import { Observable, catchError, debounceTime, firstValueFrom, forkJoin, of, switchMap, takeWhile } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { SuccessModalComponent } from 'src/app/modals/success-modal/success-modal.component';
import { emailPattern, namePattern, phoneExporter, phoneImporter, usPhonePattern } from 'src/app/utils/form-utils';
import { DealershipService } from '../../../services/dealership.service';
import { formatPhone } from 'src/app/utils/format-util';
import { SettingsService } from '@services/settings.service';
import { RpcApiResponse } from '@interfaces/api.interfaces';

@Component({
    selector: 'app-additional-admins',
    templateUrl: './additional-admins.component.html',
    styleUrls: ['./additional-admins.component.scss'],
})
export class AdditionalAdminsComponent implements OnInit, OnDestroy {
    @Output() signal: EventEmitter<AppEvent> = new EventEmitter();
    @Input() dealerName: string;
    @Input() dealerCode: string;
    @Input() token: string;
    @Input() currentUser: User;
    @Input() dealerId: number;
    admin1Form: FormGroup;
    admin2Form: FormGroup;
    admin1ExistingUser: User;
    admin2ExistingUser: User;
    jobTitleAdmin1Control: string;
    jobTitleAdmin2Control: string;
    jobTitles: JobTitle[] = [];
    expandedAdmin2 = false;
    isLoading = false;
    isComponentAlive: boolean;
    private defaultToMfa = false;
    tenantSupportEmail = '';
    tenantSupportPhone = '';

    constructor(
        private readonly errorService: ErrorService,
        private fb: FormBuilder,
        private readonly enrollmentService: EnrollmentService,
        private readonly userService: UserService,
        private readonly dialog: MatDialog,
        private readonly featureFlag: FeatureFlagService,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {
        this.defaultToMfa = this.featureFlag.getDefaultFeatureConfiguration('defaultToMfa');
    }

    ngOnInit(): void {
        this.enrollmentService.getJobTitles(this.token, null, this.dealerCode).subscribe((jobTitles) => {
            this.jobTitles = jobTitles;
        });

        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            ({ supportEmail: this.tenantSupportEmail, supportPhone: this.tenantSupportPhone } = await this.settingsService.getOemSettings(dealer.oemCode));
        });

        this.isComponentAlive = true;
        this.buildAdmin1Form();
        this.buildAdmin2Form();

        this.admin1Form.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.admin1Form.controls.email.setValue(value.trim());
            });

        this.admin2Form.controls.email.valueChanges
            .pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(500)
            )
            .subscribe((value) => {
                value.match(/\s+$/) && this.admin2Form.controls.email.setValue(value.trim());
            });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    goToWelcomeSetup() {
        const redirectTo = encodeURIComponent(`admin-welcome-guide/${this.dealerCode}/${this.dealerName}`);
        window.location.href = window.location.origin + `/welcome/setup/${redirectTo}`;
    }

    buildAdmin1Form() {
        this.admin1Form = this.fb.group({
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            mobilePhone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });
    }

    buildAdmin2Form() {
        this.admin2Form = this.fb.group({
            email: ['', Validators.compose([Validators.required, Validators.maxLength(500), Validators.pattern(emailPattern)])],
            firstName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            lastName: ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(namePattern)])],
            mobilePhone: ['', Validators.compose([Validators.required, Validators.pattern(usPhonePattern)])],
        });
    }

    admin1AddStarted() {
        return this.admin1Form.get('email').value;
    }

    admin2AddStarted() {
        return this.admin2Form.get('email').value;
    }

    checkIfUserExistsForEmail(admin1: boolean) {
        if (admin1) {
            this.userService.getUserByEmailForLogin$(this.admin1Form.controls.email.value).subscribe((user) => {
                if (user) {
                    this.admin1ExistingUser = user;
                    this.admin1Form.controls.email.patchValue(user.email);
                    this.admin1Form.controls.firstName.patchValue(user.firstName);
                    this.admin1Form.controls.lastName.patchValue(user.lastName);
                    this.admin1Form.controls.mobilePhone.patchValue(formatPhone(phoneImporter(user.phone)));
                    this.jobTitleAdmin1Control = user.jobTitle;
                } else {
                    this.admin1ExistingUser = null;
                }
            });
        } else {
            this.userService.getUserByEmailForLogin$(this.admin2Form.controls.email.value).subscribe((user) => {
                if (user) {
                    this.admin2ExistingUser = user;
                    this.admin2Form.controls.email.patchValue(user.email);
                    this.admin2Form.controls.firstName.patchValue(user.firstName);
                    this.admin2Form.controls.lastName.patchValue(user.lastName);
                    this.admin2Form.controls.mobilePhone.patchValue(formatPhone(phoneImporter(user.phone)));
                    this.jobTitleAdmin2Control = user.jobTitle;
                } else {
                    this.admin2ExistingUser = null;
                }
            });
        }
    }

    cancel() {
        this.signal.emit({ context: 'cancel', data: null });
    }

    createOrUpdateUser(
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        jobTitle: string,
        existingUser: User,
        userId?: number
    ): Observable<User> {
        if (existingUser) {
            return this.updateUser(email, firstName, lastName, phoneExporter(phone), jobTitle, userId);
        } else {
            return this.createUser(email, firstName, lastName, phoneExporter(phone), jobTitle);
        }
    }

    createUser(email: string, firstName: string, lastName: string, phone: string, jobTitle: string): Observable<User> {
        return this.userService
            .adminCreateUser$(
                this.dealerId,
                email,
                firstName,
                lastName,
                phone,
                this.currentUser.dealerAppPermissions,
                this.currentUser.systemAdministratorAtDealerId,
                null,
                null,
                this.defaultToMfa,
                true,
                false,
                jobTitle
            )
            .pipe(switchMap((nestedUser) => of(nestedUser.user)));
    }

    updateUser(email: string, firstName: string, lastName: string, phone: string, jobTitle: string, userId: number): Observable<User> {
        return this.userService.adminUpdateUser$(
            userId,
            email,
            firstName,
            lastName,
            phone,
            this.currentUser.systemAdministratorAtDealerId,
            null,
            this.dealerId,
            null,
            this.defaultToMfa,
            true,
            UserTypes.Dealer,
            jobTitle
        );
    }

    enableContinueButton() {
        if (this.admin1AddStarted() > 0 && !this.admin1Form.valid) {
            return false;
        }

        if (this.admin2AddStarted() && !this.admin2Form.valid) {
            return false;
        }

        return true;
    }

    sendWelcomeEmail(email: string): Observable<RpcApiResponse<string>> {
        return this.userService.sendWelcomeNotification(email);
    }

    submit($event: any) {
        $event.preventDefault();
        $event.stopPropagation();
        const createUserObservableList = [];
        const sendWelcomeEmailObservableList = [];

        if (this.admin1AddStarted()) {
            this.isLoading = true;

            if (this.admin1Form.valid) {
                createUserObservableList.push(
                    this.createOrUpdateUser(
                        this.admin1Form.get('email').value,
                        this.admin1Form.get('firstName').value,
                        this.admin1Form.get('lastName').value,
                        phoneExporter(this.admin1Form.get('mobilePhone').value),
                        this.jobTitleAdmin1Control,
                        this.admin1ExistingUser,
                        this.admin1ExistingUser?.userId
                    )
                );
                sendWelcomeEmailObservableList.push(this.sendWelcomeEmail(this.admin1Form.get('email').value));
            } else {
                this.errorService.showError({
                    statusCode: 500,

                    message: `Please try again and if you continue to experience issues, please contact us. 
                              
                              EZ DOCS Support
                              Phone: ${this.tenantSupportPhone}
                              Email: ${this.tenantSupportEmail}`,
                });
            }
        }

        if (this.admin2AddStarted()) {
            this.isLoading = true;
            if (this.admin2Form.valid) {
                createUserObservableList.push(
                    this.createOrUpdateUser(
                        this.admin2Form.get('email').value,
                        this.admin2Form.get('firstName').value,
                        this.admin2Form.get('lastName').value,
                        phoneExporter(this.admin2Form.get('mobilePhone').value),
                        this.jobTitleAdmin2Control,
                        this.admin2ExistingUser,
                        this.admin2ExistingUser?.userId
                    )
                );
                sendWelcomeEmailObservableList.push(this.sendWelcomeEmail(this.admin2Form.get('email').value));
            } else {
                this.errorService.showError({
                    statusCode: 500,

                    message: `Please try again and if you continue to experience issues, please contact us. 
                              
                              EZ DOCS Support
                              Phone: ${this.tenantSupportPhone}
                              Email: ${this.tenantSupportEmail}`,
                });
            }
        }

        if (this.admin1AddStarted() || this.admin2AddStarted()) {
            forkJoin(createUserObservableList)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((error) => {
                        console.error('Error occurred:', error);
                        this.isLoading = false;
                        this.errorService.showError({
                            statusCode: 500,
                            title: 'There was an error when adding the system administrator(s)',
                            message: `Please try again and if you continue to experience issues, please reach out to us.
      
                  EZ DOCS Support
                  Phone: ${this.tenantSupportPhone}
                  Email: ${this.tenantSupportEmail}`,
                        });

                        throw error;
                    })
                )
                .subscribe((response) => {
                    forkJoin(sendWelcomeEmailObservableList)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((error) => {
                                console.error('Error occurred:', error);
                                this.isLoading = false;
                                this.errorService.showError({
                                    statusCode: 500,
                                    title: 'There was an error when adding sending welcome notifications to the system administrator(s)',
                                    message: `Please try again and if you continue to experience issues, please reach out to us.
      
                  EZ DOCS Support
                  Phone: ${this.tenantSupportPhone}
                  Email: ${this.tenantSupportEmail}`,
                                });

                                throw error;
                            })
                        )
                        .subscribe(async (response) => {
                            this.isLoading = false;
                            await firstValueFrom(
                                this.dialog
                                    .open(SuccessModalComponent, {
                                        width: '700px',
                                        closeOnNavigation: true,
                                        data: {
                                            title: 'Admin User(s) Created/ Updated Successfully',
                                            content: 'New users will receive an email with instructions to complete their account setup.',
                                            btnText: 'Continue',
                                        },
                                    })
                                    .afterClosed()
                            );

                            this.goToWelcomeSetup();
                        });
                });
        } else {
            this.dialog
                .open(ConfirmModalComponent, {
                    width: '700px',
                    closeOnNavigation: true,
                    data: {
                        title: 'At Least Two Administrators Recommended',
                        richMessage: `<p>For security purposes, you should have at least two system administrators for your dealership.  If you are unable to add additional administrators now, you can do so later in the User Management section of the application.</p>
      
                    <p>If you have any questions or need help, please reach out to us.</p>
                    
                    <br /> 
                    <strong>EZ DOCS Support</strong>
                    <br /> 
                    Phone: ${this.tenantSupportPhone}
                    <br /> 
                    Email: ${this.tenantSupportEmail}`,
                        btnText: 'Continue',
                    },
                })
                .afterClosed()
                .subscribe((close) => {
                    if (close) {
                        this.goToWelcomeSetup();
                    }
                });
        }
    }
}
